import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  deleteDoc,
  query,
  where,
  serverTimestamp,
  onSnapshot
} from 'firebase/firestore';
import { db, auth } from './firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { normalizeAddress } from './addressUtils';
import { validateAlias } from './utils/aliasValidation';

/**
 * Set or update an alias for an address
 *
 * For EVM addresses: Alias is shared across all EVM chains (ethereum, polygon, bsc, etc.)
 * For non-EVM addresses: Alias is chain-specific
 *
 * @param {string} userAddress - DEPRECATED: Not used, kept for compatibility
 * @param {string} targetAddress - The address to assign an alias to
 * @param {string} alias - The alias name
 * @param {string} targetChain - The blockchain chain of the target address (defaults to 'ethereum')
 * @returns {Promise<void>}
 */
export const setAddressAlias = async (userAddress, targetAddress, alias, targetChain = 'ethereum') => {
  // Check authentication
  if (!auth.currentUser) {
    throw new Error('You must be signed in to set an alias');
  }

  if (!targetAddress || !alias) {
    throw new Error('Target address and alias are required');
  }

  // Get UID from auth (now just the address, chain-agnostic)
  const uid = auth.currentUser.uid;

  // Normalize target address for document ID
  // EVM: lowercase (0xabc...)
  // Non-EVM: preserve case (1ABC... for Bitcoin - case-sensitive)
  const targetAddressNormalized = normalizeAddress(targetAddress);
  const trimmedAlias = alias.trim();

  // Validate alias using shared validation
  const validation = validateAlias(trimmedAlias);
  if (!validation.valid) {
    throw new Error(validation.error);
  }

  // Check if alias already exists for a different address
  const existingAddress = await getAddressByAlias(trimmedAlias);
  if (existingAddress && existingAddress !== targetAddressNormalized) {
    throw new Error('This alias is already used for another address');
  }

  // Document ID is just the normalized address (chain-agnostic)
  // No need to store targetAddress as a field - it's in the doc ID
  const aliasRef = doc(db, 'addresses', uid, 'aliases', targetAddressNormalized);
  const existingDoc = await getDoc(aliasRef);

  await setDoc(aliasRef, {
    alias: trimmedAlias,
    createdAt: existingDoc.exists() ? existingDoc.data().createdAt : serverTimestamp(),
    updatedAt: serverTimestamp()
  });
};

/**
 * Get alias for a specific address
 *
 * For EVM addresses: Returns alias shared across all EVM chains
 * For non-EVM addresses: Returns chain-specific alias
 *
 * @param {string} userAddress - DEPRECATED: Not used, kept for compatibility
 * @param {string} targetAddress - The address to get alias for
 * @param {string} targetChain - The blockchain chain of the target address (defaults to 'ethereum')
 * @returns {Promise<string|null>} The alias or null if not found
 */
export const getAlias = async (userAddress, targetAddress, targetChain = 'ethereum') => {
  if (!targetAddress) return null;

  // Check authentication
  if (!auth.currentUser) return null;

  const uid = auth.currentUser.uid;
  const targetAddressNormalized = normalizeAddress(targetAddress);

  try {
    // Document ID is just the normalized address (chain-agnostic)
    const aliasRef = doc(db, 'addresses', uid, 'aliases', targetAddressNormalized);
    const aliasDoc = await getDoc(aliasRef);

    if (aliasDoc.exists()) {
      return aliasDoc.data().alias;
    }

    return null;
  } catch (error) {
    console.error('Error getting alias:', error);
    return null;
  }
};

/**
 * Get all aliases for a user
 * @param {string} userAddress - DEPRECATED: Not used, kept for compatibility
 * @returns {Promise<Object>} Map of address (e.g., "0x123...") -> alias
 */
export const getAllAliases = async (userAddress) => {
  // Check authentication
  if (!auth.currentUser) return {};

  const uid = auth.currentUser.uid;

  try {
    // Document ID is just the normalized address (chain-agnostic)
    const aliasesRef = collection(db, 'addresses', uid, 'aliases');
    const snapshot = await getDocs(aliasesRef);

    const aliases = {};
    snapshot.forEach(docSnap => {
      const targetAddress = docSnap.id; // e.g., "0x123..." or "1ABC..."
      const data = docSnap.data();
      aliases[targetAddress] = data.alias;
    });

    return aliases;
  } catch (error) {
    console.error('Error getting all aliases:', error);
    return {};
  }
};

/**
 * Get address by alias (for uniqueness checking)
 * @param {string} alias - The alias to search for
 * @returns {Promise<string|null>} The address with this alias or null
 */
export const getAddressByAlias = async (alias) => {
  if (!alias) return null;

  // Check authentication
  if (!auth.currentUser) return null;

  const uid = auth.currentUser.uid;
  const trimmedAlias = alias.trim();

  try {
    const aliasesRef = collection(db, 'addresses', uid, 'aliases');
    const q = query(aliasesRef, where('alias', '==', trimmedAlias));
    const snapshot = await getDocs(q);

    if (!snapshot.empty) {
      // Return normalized address (document ID)
      return snapshot.docs[0].id;
    }

    return null;
  } catch (error) {
    console.error('Error getting address by alias:', error);
    return null;
  }
};

/**
 * Delete an alias
 *
 * For EVM addresses: Deletes alias shared across all EVM chains
 * For non-EVM addresses: Deletes chain-specific alias
 *
 * @param {string} userAddress - DEPRECATED: Not used, kept for compatibility
 * @param {string} targetAddress - The address to remove alias from
 * @param {string} targetChain - The blockchain chain of the target address (defaults to 'ethereum')
 * @returns {Promise<void>}
 */
export const deleteAlias = async (userAddress, targetAddress, targetChain = 'ethereum') => {
  // Check authentication
  if (!auth.currentUser) {
    throw new Error('You must be signed in to delete an alias');
  }

  if (!targetAddress) {
    throw new Error('Target address is required');
  }

  const uid = auth.currentUser.uid;
  const targetAddressNormalized = normalizeAddress(targetAddress);

  // Document ID is just the normalized address (chain-agnostic)
  const aliasRef = doc(db, 'addresses', uid, 'aliases', targetAddressNormalized);
  await deleteDoc(aliasRef);
};

/**
 * Subscribe to user's aliases with real-time updates
 * @param {string} userAddress - DEPRECATED: Not used, kept for compatibility
 * @param {Function} callback - Called with aliases object on updates
 * @returns {Function} Unsubscribe function
 */
export const subscribeToAliases = (userAddress, callback) => {
  // Check authentication
  if (!auth.currentUser) {
    callback({});
    return () => {};
  }

  const uid = auth.currentUser.uid;
  const aliasesRef = collection(db, 'addresses', uid, 'aliases');

  // Use Firestore's real-time listener
  const unsubscribe = onSnapshot(aliasesRef, (snapshot) => {
    const aliases = {};
    snapshot.forEach(docSnap => {
      const targetAddress = docSnap.id; // e.g., "0x123..." or "1ABC..."
      const data = docSnap.data();
      aliases[targetAddress] = data.alias;
    });
    callback(aliases);
  }, (error) => {
    console.error('Error subscribing to aliases:', error);
    callback({});
  });

  return unsubscribe;
};

/**
 * Subscribe to a single alias with real-time updates
 *
 * For EVM addresses: Subscribes to alias shared across all EVM chains
 * For non-EVM addresses: Subscribes to chain-specific alias
 *
 * @param {string} userAddress - DEPRECATED: Not used, kept for compatibility
 * @param {string} targetAddress - The address to get alias for
 * @param {Function} callback - Called with alias string (or null) on updates
 * @param {string} targetChain - The blockchain chain of the target address (defaults to 'ethereum')
 * @returns {Function} Unsubscribe function
 */
export const subscribeToAlias = (userAddress, targetAddress, callback, targetChain = 'ethereum') => {
  if (!targetAddress) {
    callback(null);
    return () => {};
  }

  // Check authentication - if not ready, wait briefly for auth state
  if (!auth.currentUser) {
    // Set up a one-time auth state listener to wait for auth
    let unsubscribeAuth = null;
    let unsubscribeSnapshot = null;
    let settled = false;

    unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      if (settled) return; // Already handled

      if (user) {
        // Auth is ready, set up the real subscription
        settled = true;
        const uid = user.uid;
        const targetAddressNormalized = normalizeAddress(targetAddress);
        const aliasRef = doc(db, 'addresses', uid, 'aliases', targetAddressNormalized);

        unsubscribeSnapshot = onSnapshot(aliasRef, (docSnapshot) => {
          if (docSnapshot.exists()) {
            callback(docSnapshot.data().alias);
          } else {
            callback(null);
          }
        }, (error) => {
          console.error('Error subscribing to alias:', error);
          callback(null);
        });

        // Clean up the auth listener once we've set up the snapshot
        if (unsubscribeAuth) {
          unsubscribeAuth();
          unsubscribeAuth = null;
        }
      } else {
        // User is definitely not logged in
        settled = true;
        callback(null);
        if (unsubscribeAuth) {
          unsubscribeAuth();
          unsubscribeAuth = null;
        }
      }
    });

    return () => {
      if (unsubscribeAuth) unsubscribeAuth();
      if (unsubscribeSnapshot) unsubscribeSnapshot();
    };
  }

  const uid = auth.currentUser.uid;
  const targetAddressNormalized = normalizeAddress(targetAddress);

  // Document ID is just the normalized address (chain-agnostic)
  const aliasRef = doc(db, 'addresses', uid, 'aliases', targetAddressNormalized);

  // Use Firestore's real-time listener for a single document
  const unsubscribe = onSnapshot(aliasRef, (docSnapshot) => {
    if (docSnapshot.exists()) {
      callback(docSnapshot.data().alias);
    } else {
      callback(null);
    }
  }, (error) => {
    console.error('Error subscribing to alias:', error);
    callback(null);
  });

  return unsubscribe;
};
